import { useFlags } from '@app/hooks/use-flags';
import {
  selectOrganizationInfo,
  selectOrganizationStatus,
} from '@app/routes/organization/selectors';
import {
  selectOrganizations,
  selectUserProfile,
  selectUserProfileStatus,
} from '@app/store/selectors';
import { UserProfile } from '@app/store/state';
import { useAppSelector } from '@app/store/store';
import { FLAG_RINGCENTRAL_CHAT_WIDGET_SCRIPT_ID } from '@app/utilities/flags';
import { useEffect, useRef, useState } from 'react';

const buildAndSetUserIdentity = (
  userProfile: UserProfile,
  userType: string,
  isPremium: boolean,
) => {
  const userInfoForChat = {
    screenname: `${userProfile.firstName} ${userProfile.lastName}`,
    firstname: userProfile.firstName,
    lastname: userProfile.lastName,
    email: userProfile.email,
    extra_values: {
      user_id: userProfile.id,
      user_type: userType,
      is_premium: isPremium,
    },
  };
  window._chatq = window._chatq || [];
  window._chatq.push(
    ['_setIdentity', userInfoForChat],
    ['_fillCustomVariable', 'authenticated', true],
  );
};

export const RingCentralChatWidget = () => {
  const userProfile = useAppSelector(selectUserProfile);
  const userProfileStatus = useAppSelector(selectUserProfileStatus);
  const organization = useAppSelector(selectOrganizationInfo);
  const organizationStatus = useAppSelector(selectOrganizationStatus);
  const organisations = useAppSelector(selectOrganizations);
  const chatWidgetStatus = useRef<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle',
  );
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const flags = useFlags();
  const rcxChatScriptId = flags?.[FLAG_RINGCENTRAL_CHAT_WIDGET_SCRIPT_ID];

  useEffect(() => {
    if (
      !rcxChatScriptId ||
      userProfileStatus !== 'success' ||
      !userProfile ||
      ['loading', 'loaded'].includes(chatWidgetStatus.current) ||
      !organisations ||
      organisations?.length === 0
    ) {
      return;
    }

    chatWidgetStatus.current = 'loading';
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = 'rcx-chat-widget-script';
    script.src = `https://support.chat.digital.ringcentral.com/chat/${rcxChatScriptId}/loader.js?${Date.now()}`;

    document.body.appendChild(script);

    buildAndSetUserIdentity(userProfile, 'organizer', false);
    script.onload = () => {
      chatWidgetStatus.current = 'loaded';
      setIsWidgetLoaded(true);
    };

    script.onerror = () => {
      chatWidgetStatus.current = 'error';
    };

    return () => {
      const existingScript = document.getElementById('rcx-chat-widget-script');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [organisations, userProfile, userProfileStatus, rcxChatScriptId]);

  useEffect(() => {
    if (!isWidgetLoaded || userProfileStatus !== 'success' || !userProfile) {
      return;
    }

    if (organizationStatus === 'success') {
      buildAndSetUserIdentity(
        userProfile,
        'organizer',
        !!(
          organization?.paymentPlan?.planName &&
          organization?.paymentPlan?.planName.toLowerCase() !== 'free'
        ),
      );
    } else {
      buildAndSetUserIdentity(userProfile, 'organizer', false);
    }
  }, [
    userProfile,
    organizationStatus,
    organization?.paymentPlan?.planName,
    userProfileStatus,
    isWidgetLoaded,
  ]);

  return null;
};
